import {
  toWSDateString,
  useIsMobile,
  useWSSnackbar,
  WSAlert,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSGrid,
  WSInfiniteScroll,
  WSList,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import {
  Route,
  RouteComponentProps,
  useHistory,
  useLocation
} from "react-router-dom";
import { ErrorAlert } from "@components/ErrorAlert";
import {
  PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD,
  PATH_SETTINGS_PAYROLL_METHOD
} from "../../../modules/Settings/paths";
import { WSQueries } from "../../../query/WSQuery";
import {
  queriesInternalAccounts,
  queriesExternalBankAccounts,
  queriesPayeeRows
} from "@query";
import { QUERY_BULK_PAYROLL_BATCHES } from "../../../query/bulkPayrollBatch";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useQueryPayrollSettings,
  useQueryPayrollImmediatePayables
} from "../../../query/payments/queries";
import { useClientQuery } from "../../../query/users/queries";
import { paymentsService } from "../../../services/payments";
import { usersService } from "../../../services/users";
import { LayoutSetup } from "../../../shared/components/LayoutSetup";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { selectorFundingSource } from "../../../shared/selectors/selectorFundingSource";
import { selectorActiveBankingAccount } from "../../../shared/selectors/selectorActiveBankingAccount";
import { selectorFundingSourceName } from "../../../shared/selectors/selectorFundingSourceName";
import { selectorHasFundingMethod } from "../../../shared/selectors/selectorHasFundingMethod";
import { Summary } from "../../components/Summary/Summary";
import { TablePayables } from "../../components/TablePayables";
import { CompletedDeductionDetails } from "../deductions/CompletedDuductionDetails";
import { ScheduledDeductionDetails } from "../deductions/ScheduledDuductionDetails";
import {
  AlertPayrollRunInProgress,
  useHasProcessingBulkPayrollBatch
} from "./AlertPayrollRunInProgress";
import { PayableDetails } from "./PayableDetails";

const FundingSource: React.FC<WSElementProps> = ({ ...elementProps }) => {
  const userId = useUserId();
  const queryClient = useClientQuery(userId);
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const history = useHistory();

  if (
    !queryClient.data ||
    !queryPayrollSettings.data ||
    !queryExternalBankAccounts.data
  ) {
    return (
      <WSElement {...elementProps}>
        <WSLoader.Spinner />
      </WSElement>
    );
  }

  const fundingSourceName = selectorFundingSourceName(
    queryClient.data,
    queryPayrollSettings.data,
    queryExternalBankAccounts.data.data || [],
    queryInternalAccounts.data?.data?.find(selectorActiveBankingAccount)
  );

  if (
    queryClient.isLoading ||
    queryPayrollSettings.isLoading ||
    queryExternalBankAccounts.isLoading ||
    queryInternalAccounts.isLoading
  )
    return null;

  return (
    <Summary
      label={
        <WSButton.Link
          onClick={() => {
            history.push(PATH_SETTINGS_PAYROLL_METHOD);
          }}
          rightIcon="arrow-right"
          size="S"
        >
          Payroll funding method
        </WSButton.Link>
      }
      value={fundingSourceName || "–"}
    />
  );
};

export const OffCyclePayroll: React.FC<RouteComponentProps> = ({ match }) => {
  const history = useHistory();
  const location = useLocation();
  const userId = useUserId();
  const queryPayrollImmediatePayables = useQueryPayrollImmediatePayables({
    refetchOnMount: true
  });
  const queryAllPayeeRows = queriesPayeeRows.searchPayeeRows.useQueryAll();
  const queryClient = useClientQuery(userId);
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const { openSnackbar } = useWSSnackbar();

  const hasProcessingBulkPayrollBatch = useHasProcessingBulkPayrollBatch();

  const [runOffCyclePayroll, runOffCyclePayrollMeta] = useWSMutation(
    async () => {
      const payrollSettings = await paymentsService.payrollSettings.get(userId);
      const client = await usersService.client.get(userId);

      const fundingSource = selectorFundingSource(payrollSettings, client);

      if (!fundingSource) {
        throw new Error("No funding source set");
      }

      // if (fundingSource.type === FundingSourceType.InternalAccount) {
      //   const balance = await bookkeepingService.getBankingBalance();
      //   if (!balance.balance) {
      //     throw new Error("Insufficient funds in internal account");
      //   }
      // }

      try {
        await paymentsService.payApproved();
      } catch (error) {
        console.error("Off cycle payroll request failed", error);
      }
    },
    {
      onSuccess: () => {
        history.push("/member/invoices/payables");

        openSnackbar({ message: "Off-cycle payroll initiated" });
      },
      onError: error => {
        openSnackbar({ message: error.message, type: "error" });
      },
      // The v1 payroll-invoices list this used to invalidate is gone — the
      // Payroll Runs tab now reads v3 `listPayrollRuns` (WIN-15813), which this
      // v1 flow cannot reach. WIN-15814 rebuilds off-cycle payroll on v3.
      dependencies: [QUERY_BULK_PAYROLL_BATCHES]
    }
  );
  const isMobile = useIsMobile();

  const defaultCurrency = useDefaultCurrency();

  const hasFundingMethod = selectorHasFundingMethod(
    queryPayrollSettings.data,
    queryClient.data
  );

  return (
    <LayoutSetup
      wide
      onBack={() => {
        history.push("/member/invoices/payables");
      }}
    >
      <WSList gap="2XL">
        <AlertPayrollRunInProgress />

        <WSElement>
          <WSText.Heading4>Run off-cycle payroll</WSText.Heading4>
          <WSText>
            Invoices eligible for payroll must be approved, have a status of
            Pending, Open, or Overdue, and an amount of $0 or more. Invoices
            will be paid on the last possible payroll to ensure payment reaches
            the contractor by the due date.
          </WSText>
        </WSElement>

        <WSQueries
          queries={{
            queryPayrollImmediatePayables,
            queryAllPayeeRows,
            queryClient,
            queryPayrollSettings
          }}
        >
          {({
            queryPayrollImmediatePayables: {
              data: {
                data: payables,
                summary: { totalValue, listSize }
              }
            }
          }) => (
            <>
              <WSElement>
                <WSGrid>
                  <WSGrid.Item span={{ xs: "6", m: "3" }}>
                    <Summary
                      label="Total 1099s"
                      value={formatMoney(totalValue, defaultCurrency)}
                    />
                  </WSGrid.Item>

                  <WSGrid.Item span={{ xs: "6", m: "2" }}>
                    <Summary label="Total amount" value={listSize} />
                  </WSGrid.Item>

                  <WSGrid.Item span={{ xs: "6", m: "4" }}>
                    <FundingSource />
                  </WSGrid.Item>

                  {payables[0]?.labels && (
                    <WSGrid.Item span={{ xs: "6", m: "3" }}>
                      <Summary
                        label="Expected deposit date"
                        value={toWSDateString(
                          payables[0].labels?.settlementDate,
                          "monthDate"
                        )}
                      />
                    </WSGrid.Item>
                  )}
                </WSGrid>
              </WSElement>

              {!hasFundingMethod ? (
                <WSAlert
                  theme="warning"
                  icon="alert"
                  title="Funding method is missing"
                  action={{
                    text: "Add funding method",
                    onClick: () => {
                      history.push(PATH_SETTINGS_PAYROLL_ADD_FUNDING_METHOD, {
                        backPath: location.pathname
                      });
                    }
                  }}
                >
                  Please add a funding method to enable payroll
                </WSAlert>
              ) : null}

              <WSElement>
                <WSGrid>
                  <WSGrid.Item span={{ s: "6", m: "8" }}>
                    <WSText.Heading5>Included payables</WSText.Heading5>
                    <WSText>
                      To update the payables included in this payroll run, edit
                      the payable approval or pay date.
                    </WSText>
                  </WSGrid.Item>

                  <WSGrid.Item span={{ s: "6", m: "4" }}>
                    <WSFlexBox.CenterY justify="flex-end">
                      <WSButton.Primary
                        fullWidth={isMobile}
                        disabled={
                          !hasFundingMethod || hasProcessingBulkPayrollBatch
                        }
                        onClick={() => {
                          runOffCyclePayroll();
                        }}
                        loading={runOffCyclePayrollMeta.isLoading}
                      >
                        Run payroll
                      </WSButton.Primary>
                    </WSFlexBox.CenterY>
                  </WSGrid.Item>
                </WSGrid>
              </WSElement>

              <ErrorAlert
                context="OffCyclePayroll"
                error={runOffCyclePayrollMeta.error}
              />

              <Route
                path={`${match.path}/:payableId/deduction/scheduled/:deductionId`}
                component={ScheduledDeductionDetails}
              />
              <Route
                path={`${match.path}/:payableId/deduction/completed/:deductionId`}
                component={CompletedDeductionDetails}
              />
              <Route
                path={`${match.path}/:payableId`}
                component={PayableDetails}
                exact
              />

              <WSInfiniteScroll
                onLoad={() => {
                  queryPayrollImmediatePayables.fetchNextPage();
                }}
                loadMore={payables.length > 0}
                endOfList={!queryPayrollImmediatePayables.hasNextPage}
                loading={!!queryPayrollImmediatePayables.isFetchingNextPage}
              >
                <TablePayables
                  payables={payables}
                  payeeRows={queryAllPayeeRows.flatData ?? []}
                  showHeaders
                  showInvoiceNumber
                  showDateRequested
                  showDueDate
                  onRowClick={({
                    data: {
                      payable: { payableId }
                    }
                  }) => {
                    history.push(`${match.path}/${payableId}`);
                  }}
                />
                {payables.length === 0 && (
                  <WSFlexBox justify="center" mt="XL">
                    <WSText color="gray600">
                      No payables available for off-cycle payroll
                    </WSText>
                  </WSFlexBox>
                )}
              </WSInfiniteScroll>
            </>
          )}
        </WSQueries>
      </WSList>
    </LayoutSetup>
  );
};
